import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge, PriorityBadge } from '@/components/Badges';
import { TICKET_STATUS_LABEL, PRIORITY_LABEL } from '@/lib/labels';
import { Save, Loader2 } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { Card } from '@/components/ui/card';

function SLAProgress({ ticket }) {
  if (!ticket?.slaDeadline) return null;
  const opened = new Date(ticket.openedAt).getTime();
  const deadline = new Date(ticket.slaDeadline).getTime();
  const now = Date.now();
  const total = deadline - opened || 1;
  const elapsed = now - opened;
  const pct = Math.min(100, Math.max(0, (elapsed / total) * 100));
  const exceeded = now > deadline;
  const atRisk = pct > 75 && !exceeded;
  const color = exceeded ? 'bg-red-500' : atRisk ? 'bg-amber-500' : 'bg-emerald-500';
  const label = exceeded ? 'SLA excedido' : atRisk ? 'SLA em risco' : 'SLA no prazo';
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="text-slate-500">até {formatDate(ticket.slaDeadline, true)}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function TicketHeader({ ticket, canEdit, editing, onEdit, onCancel, onSave, saving, edit, setEdit, users }) {
  const assignableUsers = (users || []).filter((u) => !u.roles?.includes('CLIENT'));
  return (
    <Card className="border-slate-200 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-2xl font-bold font-mono text-slate-900">{ticket.protocol}</span>
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Aberto em {formatDate(ticket.openedAt, true)}
            {ticket.finalizedAt && ` • Finalizado em ${formatDate(ticket.finalizedAt, true)}`}
          </p>
          <div className="mt-4 max-w-xl"><SLAProgress ticket={ticket} /></div>
        </div>
        {canEdit && !editing && <Button onClick={onEdit} className="bg-blue-600 hover:bg-blue-700">Editar</Button>}
        {editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>Cancelar</Button>
            <Button onClick={onSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : <><Save className="w-4 h-4 mr-2" /> Salvar</>}
            </Button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 pt-5 border-t">
          <div>
            <Label>Status</Label>
            <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_STATUS_LABEL).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Prioridade</Label>
            <Select value={edit.priority} onValueChange={(v) => setEdit({ ...edit, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY_LABEL).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Responsável</Label>
            <Select value={edit.assignedToId || 'none'} onValueChange={(v) => setEdit({ ...edit, assignedToId: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem atribuição</SelectItem>
                {assignableUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </Card>
  );
}
